import { Injectable } from '@angular/core';
import { Http, Response } from '@angular/http';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import { Observable } from 'rxjs/Observable';
import { AccessToken } from '../../utils/accesstoken';
import { Cookie } from '../../utils/cookie';
import { TmAccount, TmAddress, TmCreditCard } from '../model/account';
import { TmErrorInfo } from '../model/error';
import {
    TmPasswordResetResponse,
    TmPasswordVerifyResponse,
    TmUpdatePasswordResponse,
} from '../model/password-response';
import {
    TmAccountRequest,
    TmCustomerSecurityQuestionAnswerRequest,
    TmPasswordInfoRequest,
    TmPasswordResetRequest,
    TmPasswordVerifyRequest,
    TmUpdatePasswordRequest,
} from '../model/request/account-request';
import { ApiBase } from './apibase.service';
import { SignIn } from './signin.service';


export class AccountBase extends ApiBase {
    private cookie = new Cookie();
    protected accountBaseUrl = this.baseUrl + '/accounts';
    protected accountBaseUrlWithEdp: string;

    constructor(protected http: Http) {
        super(http);
        if (this.cookie.UserAccessToken != undefined && this.cookie.UserAccessToken.Edp != undefined) {
            this.accountBaseUrlWithEdp = this.accountBaseUrl + '/' + this.cookie.UserAccessToken.Edp;
        } else {
            this.accountBaseUrlWithEdp = null;
        }
    }
}(AccountBase as any).ctorParameters = function () { return [ { type: Http } ]; }


export class Account extends AccountBase {
    constructor(protected http: Http) {
        super(http);
    }

    /** @description Retrieve account details for given customerEDP
     *   @return {TmAccount}
     */
    get(): Observable<TmAccount> {
        if (this.accountBaseUrlWithEdp == null) {
            this.handleAuthorizationFailure();
        }
        return this.http
            .get(this.accountBaseUrlWithEdp, { headers: this.userTokenJsonTypeHeader })
            .map((accountResp) => accountResp.json() as TmAccount)
            .catch(this.handleError);
    }

    /** @description Create a new account.
     *  @param {TmAccountRequest} accountReq AccountRequest object
     *  @return {TmAccount}
     */
    post(accountReq: TmAccountRequest): Observable<any> {
        let cookie = new Cookie();

        return this.getAppToken()
            .map((tokenResp) => tokenResp)
            .flatMap((accessToken) => {
                return this.http
                    .post(this.accountBaseUrl, accountReq, { headers: this.appTokenJsonTypeHeader })
                    .map((accountResp) => {
                        let acctData = accountResp.json() as TmAccount;
                        let accessToken = new AccessToken();

                        accessToken.Token = acctData.AccessToken;
                        accessToken.Edp = acctData.CustomerEDP;
                       
                        cookie.UserAccessTokenForSession = accessToken;
                        cookie.UserEmail = acctData.Email;

                        return;
                    })
                    .catch((accountResp) => {
                        if (accountResp.status === 401) {
                            return this.createAppToken()
                                .flatMap((token) => {
                                    cookie.AppToken = token.json().access_token;
                                    return this.http.post(this.accountBaseUrl, accountReq, {
                                        headers: this.appTokenJsonTypeHeader,
                                    });
                                })
                                .map((accountResp) => {
                                    let acctData = accountResp.json() as TmAccount;
                                    let accessToken = new AccessToken();

                                    accessToken.Token = acctData.AccessToken;
                                    accessToken.Edp = acctData.CustomerEDP;

                                    cookie.UserAccessTokenForSession = accessToken;
                                    cookie.UserEmail = acctData.Email;

                                    return;
                                });
                        } else if (accountResp.status === 417 || accountResp.status === 400) {
                            let errorInfo: TmErrorInfo = (accountResp.json() as TmErrorInfo[])[0];
                            return Observable.throw(errorInfo);
                        } else {
                            return Observable.throw(accountResp.json() as TmErrorInfo);
                        }
                    });
            });
    }
}(Account as any).ctorParameters = function () { return [ { type: Http } ]; }


export class Account_BillingAddress extends AccountBase {
    /** @description Makes billing address as default shipping address
     *    @return {TmAccount}
     */

    put(addressRequest: TmAddress): Observable<TmAccount> {
        if (this.accountBaseUrlWithEdp == null) {
            this.handleAuthorizationFailure();
        }
        let url = this.accountBaseUrlWithEdp + '/billingaddress';

        return this.http
            .put(url, addressRequest, { headers: this.userTokenJsonTypeHeader })
            .map((r: Response) => r.json())
            .catch(this.handleError);
    }
}


export class Account_ShippingAddress extends AccountBase {
    constructor(protected http: Http) {
        super(http);
    }

    private shippingAddrUrl = this.accountBaseUrlWithEdp + '/shippingaddresses';

    /** @description Add new shipping address to given account
     *   @return {TmAccount}
     */
    post(addressRequest: TmAddress): Observable<TmAccount> {
        if (this.accountBaseUrlWithEdp == null) {
            this.handleAuthorizationFailure();
        }
        return this.http
            .post(this.shippingAddrUrl, addressRequest, { headers: this.userTokenJsonTypeHeader })
            .map((r: Response) => r.json() as TmAccount)
            .catch(this.handleError);
    }

    /** @description Update existing shipping address or mark as default (by setting isDefault flag = true)
     *   @return {TmAccount}
     */
    put(addressRequest: TmAddress): Observable<TmAccount> {
        if (this.accountBaseUrlWithEdp == null) {
            this.handleAuthorizationFailure();
        }
        const url = `${this.shippingAddrUrl}/${addressRequest.Id}`;

        return this.http
            .put(url, addressRequest, { headers: this.userTokenJsonTypeHeader })
            .map((r: Response) => r.json() as TmAccount)
            .catch(this.handleError);
    }
}(Account_ShippingAddress as any).ctorParameters = function () { return [ { type: Http } ]; }


export class Account_BillingAsDefaultShippingAddress extends AccountBase {
    /** @description Makes billing address as default shipping address
     *    @return {TmAccount}
     */
    post(): Observable<TmAccount> {
        if (this.accountBaseUrlWithEdp == null) {
            this.handleAuthorizationFailure();
        }
        let url = this.accountBaseUrlWithEdp + '/defaultshippingaddress';

        return this.http
            .post(url, '', { headers: this.userTokenJsonTypeHeader })
            .map((r: Response) => r.json())
            .catch(this.handleError);
    }
}


export class Account_Password extends AccountBase {
    /** @description Update account's password
     *   @return {string}
     */
    put(pwdRequest: TmUpdatePasswordRequest): Observable<string> {
        if (this.accountBaseUrlWithEdp == null) {
            this.handleAuthorizationFailure();
        }

        let url = this.accountBaseUrlWithEdp + '/password';

        return this.http
            .put(url, pwdRequest, { headers: this.userTokenJsonTypeHeader })
            .map((r: Response) => r.json() as string)
            .catch(this.handleError);
    }
}

export class PasswordReset extends ApiBase {
    /** @description Reset password
     *   @return {TmPasswordResetResponse}
     */
    post(pwdRequest: TmPasswordResetRequest): Observable<TmPasswordResetResponse> {
        let url = this.baseUrl + '/passwordreset';

        return this.http
            .post(url, pwdRequest, { headers: this.appTokenJsonTypeHeader })
            .map((r: Response) => r.json() as TmPasswordResetResponse)
            .catch(this.handleError);
    }
}


export class PasswordReset_Update extends ApiBase {
    /** @description Update password
     *   @return {TmUpdatePasswordResponse}
     */
    post(pwdRequest: TmUpdatePasswordRequest): Observable<TmUpdatePasswordResponse> {
        let url = this.baseUrl + '/passwordupdate';

        return this.http
            .post(url, pwdRequest, { headers: this.appTokenJsonTypeHeader })
            .map((r: Response) => r.json() as TmUpdatePasswordResponse)
            .catch(this.handleError);
    }
}


export class PasswordInfo extends ApiBase {
    /** @description get password info
     *   @return {string}
     */
    post(pwdRequest: TmPasswordInfoRequest): Observable<string> {
        let url = this.baseUrl + '/passwordinfo';

        return this.getAppToken()
            .map((tokenResp) => tokenResp)
            .flatMap((accessToken) => {
                return this.http
                    .post(url, pwdRequest, { headers: this.appTokenJsonTypeHeader })
                    .map((r: Response) => r.json() as string)
                    .catch(this.handleError);
            });
    }
}


export class PasswordVerify extends ApiBase {
    /** @description verify customer details by email, phone, postal code and security question answer
     *   @return {TmPasswordVerifyResponse}
     */
    post(pwdRequest: TmPasswordVerifyRequest): Observable<TmPasswordVerifyResponse> {
        let url = this.baseUrl + '/passwordverify';

        return this.http
            .post(url, pwdRequest, { headers: this.appTokenJsonTypeHeader })
            .map((r: Response) => r.json() as TmPasswordVerifyResponse)
            .catch(this.handleError);
    }
}


export class Account_SecurityQuestion extends AccountBase {
    /** @description Add security question
     *   @return {number}
     */
    post(secRequest: TmCustomerSecurityQuestionAnswerRequest): Observable<number> {
        if (this.accountBaseUrlWithEdp == null) {
            this.handleAuthorizationFailure();
        }
        let url = this.accountBaseUrlWithEdp + '/securityquestion';

        return this.http
            .post(url, secRequest, { headers: this.userTokenJsonTypeHeader })
            .map((r: Response) => r.json() as number)
            .catch(this.handleError);
    }

    /** @description Update security question
     *   @return {number}
     */
    put(secRequest: TmCustomerSecurityQuestionAnswerRequest): Observable<number> {
        if (this.accountBaseUrlWithEdp == null) {
            this.handleAuthorizationFailure();
        }
        let url = this.accountBaseUrlWithEdp + '/securityquestion';

        return this.http
            .put(url, secRequest, { headers: this.userTokenJsonTypeHeader })
            .map((r: Response) => r.json() as number)
            .catch(this.handleError);
    }

    
}

export class Account_NewsletterSubscription extends AccountBase {
    /** @description Subscribe to newsletter
     *    @return {}
     */
    post(): Observable<any> {
        let url = this.accountBaseUrlWithEdp + '/newslettersubscription';

        return this.http
            .post(url, '', { headers: this.userTokenJsonTypeHeader })
            .map((r: Response) => r.json())
            .catch(this.handleError);
    }
}


export class NewsletterSubscription extends ApiBase {
    /** @description Verify newsletter subscription
     *    @return {}
     */
    post(emailAddress: string): Observable<boolean> {
        let url = this.baseUrl + '/newslettersubscription?emailAddress=' + emailAddress;

        return this.http
            .post(url, { headers: this.postHeaders })
            .map((r: Response) => r.json() as boolean)
            .catch(this.handleError);
    }
}


export class Account_CreditCard extends AccountBase {
    private creditCardUrl = this.accountBaseUrlWithEdp + '/creditcards';

    /** @description Adds credit card to given account
     *    @return {}
     */
    post(creditCardReq: TmCreditCard): Observable<TmAccount> {
        if (this.accountBaseUrlWithEdp == null) {
            this.handleAuthorizationFailure();
        }
        return this.http
            .post(this.creditCardUrl, creditCardReq, { headers: this.userTokenJsonTypeHeader })
            .map((r: Response) => r.json() as TmAccount)
            .catch(this.handleErrorWithDetails);
    }
    /** @description Updates given credit card
     *    @return {}
     */
    put(creditCardReq: TmCreditCard): Observable<TmAccount> {
        if (this.accountBaseUrlWithEdp == null) {
            this.handleAuthorizationFailure();
        }
        const url = `${this.creditCardUrl}/${creditCardReq.Id}`;

        return this.http
            .put(url, creditCardReq, { headers: this.userTokenJsonTypeHeader })
            .map((r: Response) => r.json() as TmAccount)
            .catch(this.handleErrorWithDetails);
    }

    /** @description Removes given credit card from the account
     *    @return {}
     */
    delete(creditCardReq: TmCreditCard): Observable<TmAccount> {
        if (this.accountBaseUrlWithEdp == null) {
            this.handleAuthorizationFailure();
        }
        const url = `${this.creditCardUrl}/${creditCardReq.Id}`;

        return this.http
            .delete(url, { headers: this.userTokenJsonTypeHeader })
            .map((r: Response) => r.json() as TmAccount)
            .catch(this.handleError);
    }
}



// WEBPACK FOOTER //
// C:/AzureDevOpsAgent/_work/3/s/TSC.Web.WebFactory/node_modules/ngc-webpack??ref--0-1!C:/AzureDevOpsAgent/_work/3/s/TSC.Web.WebFactory/node_modules/angular2-template-loader!../tscapi/service/account.service.ts